import { useLayoutEffect, useRef, useState } from 'react';

/**
 * Freezes the first measured container width as the chart's SVG "design"
 * width — geometry (paths, bars, padding, ticks) is built against it once
 * and never recomputed on resize. The container is then sized via
 * `aspect-ratio` (design width : `height`) instead of a literal pixel
 * height, so on a narrower screen the whole chart — line/bars, gridlines,
 * fonts, markers, padding — scales down together as one unit (the browser
 * scales the SVG as a whole) instead of the plot geometry recomputing and
 * squeezing points/bars together while text/strokes stayed fixed size.
 *
 * `scale` (rendered width / design width) is only needed to translate
 * design-space coordinates into real on-screen pixels for the HTML
 * tooltip overlay, which isn't part of the scaled SVG.
 *
 * @param {number} height
 * @param {number} [fallbackWidth] used only for the single frame before the
 *   container is first measured
 */
export function useChartSize(height, fallbackWidth = 600) {
  const containerRef = useRef(null);
  const [designWidth, setDesignWidth] = useState(0);
  const [renderedWidth, setRenderedWidth] = useState(0);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    // Seed synchronously so the first paint already has a real width.
    const initial = el.getBoundingClientRect().width;
    setDesignWidth(initial);
    setRenderedWidth(initial);
    const ro = new ResizeObserver(([entry]) => {
      if (!entry) return;
      const next = entry.contentRect.width;
      setRenderedWidth(next);
      setDesignWidth((prev) => prev || next);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const w = designWidth || fallbackWidth;
  const scale = designWidth ? renderedWidth / designWidth : 1;
  const containerStyle = designWidth ? { aspectRatio: `${w} / ${height}` } : { height };

  return { containerRef, w, scale, containerStyle };
}
